import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { getCmdbObjectTypeByIdObjectsUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/object-type.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { toCmdbObjectTypeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type {
	ReferencedObjectSearchResponse,
	ReferencedObjectSearchRequestArgs,
} from '../common/types.tsx';

export const useReferencedObjectsSearch = () => {
	const { workspaceId } = useWorkspaceContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const request = useCallback(
		async ({ objectProperties, attributeType, query }: ReferencedObjectSearchRequestArgs) => {
			if (workspaceId) {
				try {
					const { id, iql, includeChildObjectTypes, referenceObjectType } = attributeType;
					const objectTypeId = toCmdbObjectTypeId(referenceObjectType.id);
					const payload = {
						objectInEntry: objectProperties,
						includeChildren: includeChildObjectTypes,
						selectedAttribute: id,
						query,
						iql,
					};
					const response = await performPostRequest(
						getCmdbObjectTypeByIdObjectsUrl(workspaceId, objectTypeId),
						{ body: JSON.stringify(payload) },
					);

					fireTrackAnalytics(createAnalyticsEvent({}), 'insightSearchReferencedObjects succeeded');

					return response;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireErrorAnalytics({
						error,
						meta: {
							id: 'insightSearchReferencedObjects',
							teamName: 'falcons',
							packageName: 'jiraServicedeskInsightEditableReferencedObjectPicker',
						},
					});
					throw error;
				}
			}

			return undefined;
		},
		[workspaceId, createAnalyticsEvent],
	);

	return useService<ReferencedObjectSearchResponse, ReferencedObjectSearchRequestArgs>(request);
};
